import { ArrowRight, History } from 'lucide-react';
import type { ReactNode } from 'react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/utils/cn';

import { EmptyState } from './states';
import { Timeline, type TimelineEntry } from './timeline';

/**
 * Change history with before and after.
 *
 * On a financial record this is not a nicety. "Who changed this contribution's fund,
 * and what was it before" is a question a church treasurer will eventually have to
 * answer to a board, and the answer has to be in the product rather than in a log
 * file only Trinity can read.
 */

export type AuditRelatedKind = 'contribution' | 'fund' | 'href';

export interface AuditRelated {
  label: string;
  kind: AuditRelatedKind;
  /** Contribution or fund id when kind is not href. */
  id?: string;
  /** Fallback route when no dialog exists for this record type. */
  href?: string;
}

export interface AuditChange {
  /** Already translated to the field's label — never the raw column name. */
  field: string;
  before: string | null;
  after: string | null;
}

export interface AuditEntry {
  id: string;
  at: string;
  /** "Sarah Mensah updated this contribution" — assembled by the caller. */
  summary: string;
  /** Extra context under the summary — reason, receipt number, date. */
  description?: string;
  actor?: string;
  changes?: AuditChange[];
  /** Related record the treasurer can open without leaving the trail. */
  related?: AuditRelated;
}

export interface AuditTrailPanelProps {
  title: string;
  entries: AuditEntry[];
  loading?: boolean;
  emptyTitle: string;
  emptyBody?: string;
  /** For a value that was previously unset. */
  emptyValueLabel: string;
  /** Open a related contribution, fund, or fallback route. */
  onOpenRelated?: (related: AuditRelated) => void;
  className?: string;
}

const changeRow = (change: AuditChange, emptyValueLabel: string): ReactNode => {
  const hasBefore = change.before != null && change.before !== '';
  const hasAfter = change.after != null && change.after !== '';

  if (!hasBefore && hasAfter) {
    return <dd className="text-fg">{change.after}</dd>;
  }

  return (
    <dd className="flex items-center gap-1.5">
      <span className="text-fg-tertiary line-through">
        {change.before ?? emptyValueLabel}
      </span>
      <ArrowRight aria-hidden className="h-3 w-3 text-fg-tertiary" />
      <span className="text-fg">{change.after ?? emptyValueLabel}</span>
    </dd>
  );
};

export const AuditTrailPanel = ({
  title,
  entries,
  loading = false,
  emptyTitle,
  emptyBody,
  emptyValueLabel,
  onOpenRelated,
  className,
}: AuditTrailPanelProps) => {
  const timelineEntries: TimelineEntry[] = entries.map((entry) => ({
    id: entry.id,
    at: entry.at,
    title: entry.summary,
    description: entry.description,
    actor: entry.actor,
    icon: <History className="h-4 w-4" />,
    content:
      entry.changes?.length || entry.related ? (
        <div className="space-y-2">
          {entry.changes && entry.changes.length > 0 ? (
            <dl className="space-y-1 rounded-md border border-border bg-surface-sunken p-2.5">
              {entry.changes.map((change) => (
                <div
                  key={change.field}
                  className="flex flex-wrap items-baseline gap-x-2 text-caption"
                >
                  <dt className="font-medium text-fg-secondary">{change.field}</dt>
                  {changeRow(change, emptyValueLabel)}
                </div>
              ))}
            </dl>
          ) : null}
          {entry.related && onOpenRelated ? (
            <button
              type="button"
              onClick={() => onOpenRelated(entry.related!)}
              className="inline-flex text-body-sm font-medium text-brand hover:underline"
            >
              {entry.related.label}
            </button>
          ) : null}
        </div>
      ) : undefined,
  }));

  return (
    <Card className={cn(className)}>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-4" aria-busy>
            {Array.from({ length: 3 }, (_, index) => (
              <div key={index} className="flex gap-3">
                <Skeleton className="h-8 w-8 rounded-full" />
                <div className="flex-1 space-y-1.5">
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-3 w-24" />
                </div>
              </div>
            ))}
          </div>
        ) : entries.length === 0 ? (
          <EmptyState title={emptyTitle} body={emptyBody} />
        ) : (
          <Timeline entries={timelineEntries} />
        )}
      </CardContent>
    </Card>
  );
};
